import { useTranslation } from 'react-i18next';
import { formatCertificateError } from '../../lib/certificateError';
import { useEffect } from 'react';

/**
 * Alert Component - Global alert/notification system
 * 
 * @param {Object} props
 * @param {string} props.type - Alert type: 'error', 'success', 'warning', 'info'
 * @param {string} props.message - Alert message
 * @param {Function} props.onClose - Called when alert is dismissed
 * @param {number} props.duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
 */
function Alert({ type = 'error', message, onClose, duration = 5000 }) {
  const { t, i18n } = useTranslation();
  const localizedMessage = formatCertificateError(message, t, i18n.resolvedLanguage || i18n.language);
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  if (!message) return null;

  const typeConfig = {
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-500',
      icon: 'close',
      title: 'Error'
    },
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-500',
      icon: 'check',
      title: 'Éxito'
    },
    warning: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-500',
      icon: 'info',
      title: 'Advertencia'
    },
    info: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-500',
      icon: 'info',
      title: 'Información'
    }
  };

  const config = typeConfig[type] || typeConfig.error;

  return (
    <div className="fixed right-4 top-4 z-[10000] w-[calc(100%-2rem)] max-w-sm pointer-events-none sm:w-full">
      <div 
        className={`pointer-events-auto w-full ${config.bgColor} ${config.borderColor} border rounded-xl shadow-xl animate-fade-in`}
        role="alert"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={`flex-shrink-0 ${config.iconColor}`}>
              <svg 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth="2" 
                stroke="currentColor"
              >
                {type === 'error' && (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" 
                  />
                )}
                {type === 'success' && (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                )}
                {(type === 'warning' || type === 'info') && (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" 
                  />
                )}
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className={`text-sm font-semibold ${config.textColor} mb-1`}>
                {config.title}
              </h3>
              <p className={`text-sm ${config.textColor} break-words`}>
                {localizedMessage}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className={`flex-shrink-0 inline-flex rounded-lg p-1.5 ${config.textColor} hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent transition-colors`}
              aria-label="Cerrar"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path 
                  fillRule="evenodd" 
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                  clipRule="evenodd" 
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Alert;
